import MuiAlert, { AlertColor, AlertProps } from '@mui/material/Alert';
import React, {
  ReactNode,
  createContext,
  forwardRef,
  useCallback,
  useMemo,
  useState,
} from 'react';
import Snackbar from '@mui/material/Snackbar';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';

type Props = {
  autoHide?: 'stay' | number;
  handleOnClose?: () => void;
  message: ReactNode;
  severity?: AlertColor;
};

export type SnackBarProviderProps = {
  hideSnackBar: () => void;
  showSnackBar: (props: Props) => void;
};
export const SnackBarContext = createContext<SnackBarProviderProps>({
  hideSnackBar: () => undefined,
  showSnackBar: (props: Props) => undefined,
});

export default function SnackBarProvider(
  props: Readonly<{ children: ReactNode }>
) {
  const [snackBar, setSnackBar] = useState<Props | null>(null);

  const showSnackBar = useCallback((props: Props) => {
    setSnackBar(props);
  }, []);

  const hideSnackBar = useCallback(() => {
    setSnackBar(null);
    snackBar?.handleOnClose?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const valueProps = useMemo(() => ({ hideSnackBar, showSnackBar }), []);

  return (
    <>
      <SnackBar
        autoHide={snackBar?.autoHide}
        handleOnClose={hideSnackBar}
        message={snackBar?.message}
        open={Boolean(snackBar)}
        severity={snackBar?.severity}
      />

      <SnackBarContext.Provider value={valueProps}>
        {props.children}
      </SnackBarContext.Provider>
    </>
  );
}

const Alert = forwardRef<HTMLDivElement, AlertProps>(function Alert(
  props,
  ref
) {
  return <MuiAlert elevation={6} ref={ref} {...props} />;
});

export function SnackBar(props: Readonly<Props> & { open?: boolean }) {
  return props.open ? (
    <Snackbar
      action={
        <IconButton
          aria-label="close"
          color="inherit"
          onClick={props.handleOnClose}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      }
      anchorOrigin={{
        horizontal: 'center', //For Position changes ;
        vertical: 'top',
      }}
      autoHideDuration={
        props.autoHide === 'stay' ? undefined : props.autoHide ?? 3000
      }
      onClose={props.handleOnClose}
      open={props.open}
      sx={{ top: '16px' }} //For Position changes ;
    >
      <Alert
        onClose={props.handleOnClose}
        severity={props.severity}
        sx={{ minWidth: '180px' }} //For size changes ;
      >
        {props.message}
      </Alert>
    </Snackbar>
  ) : null;
}
